import { ShieldCheck, Lock, FileText, EyeOff, Users, Mail, RefreshCw } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="container mx-auto py-12 px-4">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <ShieldCheck className="h-12 w-12 text-primary" />
        </div>
        <h1 className="text-4xl font-heading font-bold text-gray-900 mb-4">
          Privacy Policy
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Last Updated: April 20, 2025</p>
      </div>

      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
        {/* Summary Section */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Overview</h2>
          <p className="text-gray-600">
            Cause Connect Pte. Ltd. ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains
            how we collect, use, and safeguard your personal data when you use our platform at <strong>causeconnect.ai</strong>.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" /> Information We Collect
          </h2>
          <p className="text-gray-600 mb-4">We may collect personal information, including:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>Name, email address, and contact details</li>
            <li>Organization details and role</li>
            <li>Volunteer preferences, activities, and event history</li>
            <li>Billing and transaction data for paid services</li>
            <li>Technical data (IP address, browser type, usage logs)</li>
            <li>Cookies and tracking information</li>
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> How We Use Your Information
          </h2>
          <p className="text-gray-600 mb-4">We use collected information for purposes such as:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>Providing and improving our services</li>
            <li>Sending service updates, newsletters, and event invitations</li>
            <li>Detecting and preventing fraud or security issues</li>
            <li>Complying with legal obligations</li>
          </ul>
        </section>

        {/* Cookies & Tracking Technologies */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <EyeOff className="w-6 h-6 text-primary" /> Cookies & Tracking Technologies
          </h2>
          <p className="text-gray-600">
            We use cookies and tracking tools to analyze usage, improve our services, and personalize content. You can manage
            cookie preferences through your browser settings.
          </p>
        </section>

        {/* Data Sharing & Third Parties */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Lock className="w-6 h-6 text-primary" /> Data Sharing & Third-Party Services
          </h2>
          <p className="text-gray-600 mb-4">We do not sell your personal data. However, we may share information with:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>Service providers (hosting, analytics)</li>
            <li>Payment processors for billing transactions</li>
            <li>Legal authorities, if required by law</li>
          </ul>
        </section>

        {/* Data Security */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" /> Data Security
          </h2>
          <p className="text-gray-600">
            We implement robust security measures to protect your data from unauthorized access, alteration, or misuse.
            Regular audits and encryption ensure your information is secure.
          </p>
        </section>

        {/* Your Rights */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> Your Rights & Choices
          </h2>
          <p className="text-gray-600 mb-4">Depending on your location, you may have the right to:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2">
            <li>Access and obtain a copy of your personal data</li>
            <li>Request corrections to inaccurate information</li>
            <li>Request deletion of your data ("Right to be Forgotten")</li>
            <li>Opt-out of marketing communications</li>
            <li>Restrict or object to certain data processing activities</li>
          </ul>
          <p className="text-gray-600 mt-4">
            To exercise these rights, contact us at <strong>support@causeconnect.ai</strong>.
          </p>
        </section>

       {/* Data Protection & Compliance */}
<section className="mb-8">
  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
    <FileText className="w-6 h-6 text-primary" /> Data Protection & Compliance
  </h2>
  <p className="text-gray-600 mb-4">
    We adhere to industry standards and regulatory best practices to ensure the highest level of privacy and security.
  </p>

  <ul className="list-disc list-inside text-gray-600 space-y-2">
    <li>
      <strong>DSR Compliant:</strong> We comply with Data Subject Rights (DSR) regulations, ensuring users can access, modify, or delete their personal data upon request.
    </li>
    <li>
      <strong>Privacy by Design:</strong> Our platform is built with privacy at its core, ensuring data minimization, encryption, and secure storage of all personal information.
    </li>
    <li>
      <strong>COPPA Compliant:</strong> We comply with the Children’s Online Privacy Protection Act (COPPA) and do not knowingly collect data from children under 13.
    </li>
  </ul>
</section>

        {/* Policy Updates */}
        <section className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <RefreshCw className="w-6 h-6 text-primary" /> Updates to This Policy
          </h2>
          <p className="text-gray-600">
            We may update this Privacy Policy periodically. Changes will be posted on this page, and if significant,
            we will notify you via email or in-app alerts.
          </p>
        </section>

        {/* Contact Information */}
        <section>
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" /> Contact Us
          </h2>
          <p className="text-gray-600">
            If you have any questions about this Privacy Policy, please contact us at <strong>privacy@causeconnect.ai</strong>.
          </p>
        </section>
      </div>
    </div>
  );
}
